import { z } from "zod";
/**
 * `GET /me/exercise-records/:exerciseId` — one exercise's all-time records
 * and its per-session series (`D199`), what the exercise sheet's "Recordes"
 * and "Gráficos" tabs draw.
 *
 * ── WHY A SERVER AGGREGATE AND NOT `GET /me/exercise-history` ──────────────
 * Both tabs ask all-time questions. Through the history they would need
 * every set of the exercise the person ever logged, on every open of the
 * sheet, and that payload only grows. Here it is a handful of marks and ONE
 * point per session. The sheet's "Histórico" still reads the history, a
 * window at a time — it is a list of sets, which is what that read is.
 *
 * ── WORKING SETS ONLY ───────────────────────────────────────────────────────
 * A warm-up set (`D198`) counts toward nothing here: no record, no session
 * point, no volume. Drop and failure sets count like working ones.
 *
 * ── THE ESTIMATE IS EPLEY, THE SUMMARY'S OWN ───────────────────────────────
 * `bestE1rm` and every `bestE1rm` of a point are the estimate the session
 * summary's "novo recorde" uses (`@repo/training`'s `e1rm.ts`), so the two
 * screens cannot disagree about which set was the best.
 */
/** The rep counts the rep-max table covers. */
export const REP_MAX_REPS = 12;
/** One record, and the set that made it. */
export const exerciseRecordMark = z.object({
    /** The number the record is about: kg, reps, kg × reps, seconds or metres. */
    value: z.number(),
    /** The set's load and reps; both `null` on a session-volume record. */
    load: z.number().nullable(),
    reps: z.int().nullable(),
    performedAt: z.iso.datetime(),
    sessionId: z.string().min(1),
});
/** One session's figures for the exercise — a chart's point. */
export const exerciseSessionPoint = z.object({
    sessionId: z.string().min(1),
    /** The session's first working set of the exercise: the chart's x. */
    performedAt: z.iso.datetime(),
    sets: z.int().nonnegative(),
    bestE1rm: z.number().nullable(),
    heaviestLoad: z.number().nullable(),
    mostReps: z.int().nullable(),
    /** `Σ load × reps`; `null` when no set had both (bodyweight, holds). */
    volume: z.number().nullable(),
    /** The longest single hold of the session, in seconds. */
    longestDurationSeconds: z.number().nullable(),
    /** The session's distance, summed, in metres. */
    distanceMetres: z.number().nullable(),
});
export const exerciseRecordsOutput = z.object({
    records: z.object({
        bestE1rm: exerciseRecordMark.nullable(),
        heaviestLoad: exerciseRecordMark.nullable(),
        mostReps: exerciseRecordMark.nullable(),
        bestSetVolume: exerciseRecordMark.nullable(),
        bestSessionVolume: exerciseRecordMark.nullable(),
        longestDuration: exerciseRecordMark.nullable(),
        longestDistance: exerciseRecordMark.nullable(),
    }),
    /**
     * 1..{@link REP_MAX_REPS} reps: the heaviest load lifted for AT LEAST that
     * many, so the column never rises as the reps do. `best: null` where no set
     * reached that rep count with a load.
     */
    repMaxes: z.array(z.object({
        reps: z.int().min(1).max(REP_MAX_REPS),
        best: exerciseRecordMark.nullable(),
    })),
    /** Oldest first — chart order. Empty when the exercise was never logged. */
    sessions: z.array(exerciseSessionPoint),
});
//# sourceMappingURL=exercise-records.js.map